import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Heading,
  Container,
  Icon,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { FaUserMd, FaEnvelope, FaLock } from 'react-icons/fa';
import { checkLockout, recordFailedAttempt, clearAttempts } from '../../utils/loginRateLimiter';

const DoctorLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  // Check for email in URL query params
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Check lockout before attempting login
    const lockout = checkLockout('doctor');
    if (lockout.locked) {
      toast({
        title: 'Account Locked',
        description: `Too many failed attempts. Try again in ${lockout.remainingMinutes} minutes.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    try {
      // Find doctor by email (use limit(2) and check count to avoid "single row" API error)
      const { data: rows, error } = await supabase
        .from('doctors')
        .select('*')
        .eq('email', email.trim().toLowerCase())
        .limit(2);

      if (error) throw error;

      const doctorData = Array.isArray(rows) && rows.length === 1 ? rows[0] : null;
      if (!doctorData) {
        const result = recordFailedAttempt('doctor');
        const msg = Array.isArray(rows) && rows.length > 1
          ? 'Multiple accounts use this email. Contact your administrator.'
          : result.locked
            ? `Too many failed attempts. Try again in ${result.remainingMinutes} minutes.`
            : `No doctor found with this email. ${result.attemptsRemaining} attempts remaining.`;
        toast({
          title: 'Invalid Credentials',
          description: msg,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Verify secret key if one is set on the doctor record
      if (doctorData.secret_key) {
        if (!password || password.trim() !== doctorData.secret_key) {
          const result = recordFailedAttempt('doctor');
          toast({
            title: 'Invalid Credentials',
            description: result.locked
              ? `Too many failed attempts. Try again in ${result.remainingMinutes} minutes.`
              : `Incorrect secret key. ${result.attemptsRemaining} attempts remaining.`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
      }

      clearAttempts('doctor');

      // Update last_login_at timestamp
      const now = new Date().toISOString();
      await supabase
        .from('doctors')
        .update({ last_login_at: now })
        .eq('id', doctorData.id);

      // Store doctor session
      const doctorSession = {
        id: doctorData.id,
        full_name: doctorData.full_name,
        email: doctorData.email,
        clinic_name: doctorData.clinic_name,
        agreement_signature_url: doctorData.agreement_signature_url || null,
        loginTime: now,
      };

      localStorage.setItem('doctorSession', JSON.stringify(doctorSession));

      toast({
        title: 'Login Successful',
        description: `Welcome, Dr. ${doctorData.full_name}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/doctor/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: 'Login Failed',
        description: error.message || 'An error occurred during login.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center" py={8}>
      <Container maxW="md">
        <VStack spacing={6}>
          <VStack spacing={2}>
            <Icon as={FaUserMd} boxSize={12} color="blue.500" />
            <Heading size="lg">Doctor Portal</Heading>
            <Text color="gray.600">Sign in to view your patients' results</Text>
          </VStack>

          <Box w="100%" bg="white" p={8} borderRadius="lg" boxShadow="md">
            <form onSubmit={handleLogin}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Email Address</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaEnvelope} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      type="email"
                      placeholder="doctor@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Secret Key</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaLock} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      type="password"
                      placeholder="Enter your secret key"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </InputGroup>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  width="100%"
                  isLoading={loading}
                  loadingText="Signing in..."
                >
                  Sign In
                </Button>
              </VStack>
            </form>
          </Box>

          <Text fontSize="sm" color="gray.500" textAlign="center">
            Forgot your key? You can reset it from inside the portal, or contact your administrator.
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};

export default DoctorLogin;
